import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../components/ui/Alert';
import { AudioRoomProvider } from '../../features/audio/AudioRoomProvider';
import { useBoard, useRuleSet } from '../../features/board/api';
import { GroupBoardScreen } from '../../features/board/group/GroupBoardScreen';
import { ChatStoreProvider } from '../../features/chat/ChatStoreProvider';
import { ConnectionBanner } from '../../features/session/ConnectionBanner';
import { writeLastSession } from '../../features/session/lastSession';
import { SessionSocketProvider } from '../../features/session/SessionSocketProvider';
import { SessionStoreProvider } from '../../features/session/SessionStoreProvider';
import { sessionQueryKey, useMyParticipant, useSession } from '../../features/session/useSession';
import { GameLayout } from '../../layouts/GameLayout';
import { errorKeyFor } from '../../lib/errors';
import { isLocale } from '../../lib/i18n';
import { trpcClient } from '../../lib/trpc';

/**
 * Spec 12.2 `/:lang/play/:sessionId — facilitated board`.
 *
 * The only screen in the player-facing app that opens a socket. Spec 4.5: "a single
 * Socket.IO connection per browser tab, established only on game routes".
 */
export const Route = createFileRoute('/$lang/play/$sessionId')({ component: Page });

function Page(): React.ReactNode {
  const { lang, sessionId } = Route.useParams();

  /* Leave a pointer so the next visit can offer a rejoin or, for a planned session that has
   * since ended, its summary (ADR — solo auto-abandon on idle/exit; the facilitated banner
   * reuses the same pointer). Ids only — never a token (hard rule 7). */
  useEffect(() => {
    writeLastSession({ sessionId, mode: 'facilitated', lang });
  }, [sessionId, lang]);

  /*
   * Read the effective `chatEnabled` from the same cached `session.get` the board uses (same
   * query key), so the socket provider knows whether to wire chat without a second request.
   * It defaults false until the query resolves; the provider flips it on without reconnecting.
   */
  const chatEnabled =
    useQuery({
      queryKey: sessionQueryKey(sessionId),
      queryFn: () => trpcClient.session.get.query({ sessionId }),
      staleTime: Infinity,
      retry: false,
    }).data?.chatEnabled ?? false;

  return (
    <GameLayout lang={lang}>
      {/*
       * Store first, then the chat store, then the socket: the socket writes into both stores,
       * and a provider cannot use a context that is mounted inside it.
       */}
      <SessionStoreProvider sessionId={sessionId}>
        <ChatStoreProvider sessionId={sessionId}>
          <SessionSocketProvider sessionId={sessionId} chatEnabled={chatEnabled}>
            {/* One shared LiveKit room for both the Comm Block and the participant rail. */}
            <AudioRoomProvider sessionId={sessionId}>
              <ConnectionBanner />
              <FacilitatedSession sessionId={sessionId} lang={lang} />
            </AudioRoomProvider>
          </SessionSocketProvider>
        </ChatStoreProvider>
      </SessionStoreProvider>
    </GameLayout>
  );
}

function FacilitatedSession({
  sessionId,
  lang,
}: {
  readonly sessionId: string;
  readonly lang: string;
}): React.ReactNode {
  const { t } = useTranslation(['common', 'board']);
  const locale = isLocale(lang) ? lang : 'en';

  const { session, error } = useSession(sessionId);
  const me = useMyParticipant(sessionId);
  const board = useBoard(locale, session?.boardId);
  const ruleSet = useRuleSet(session?.ruleSetId);

  if (error !== null && error !== undefined) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <Alert tone="error">{t(errorKeyFor(error))}</Alert>
      </div>
    );
  }

  if (board.data === undefined || session === null) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <p className="text-sm text-ink-muted">{t('board:session.loading')}</p>
      </div>
    );
  }

  return (
    <GroupBoardScreen
      session={session}
      board={board.data}
      ruleSet={ruleSet.data}
      lang={locale}
      meParticipantId={me.participantId}
    />
  );
}
